import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from '../value.adapter';
import _ from 'lodash';
import { LinkedService } from '../../linkedservice/linkedservice';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { linkedServiceReferenceToResource } from '../../../shared/utils/linkedservice-parameter.util';
import {
    convertParametersToModel,
    fillParameterValuesForResource
} from '../../../shared/utils/parameter-converter.util';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ExecuteWranglingDataflowActivity } from 'app/model/pipeline/execute-wrangling-dataflow.activity';
import { WranglingDataflow } from 'app/model/dataflow/wranglingDataflow';
import { ParameterValue } from 'app/model/base/parameter-value';
import { Dataset } from 'app/model/dataset/dataset';
import { DataFlowSink, FileNameOption } from 'app/model/dataflow/dataflow-sink';
import { Owned } from 'app/model/base/named';
import { SinkAdapter } from '../dataflow/sink.adapter';
import { FLOW, IDataFlowQuery, IDataFlowSink } from '../dataflow/IDataflow';
import { PowerQuerySink } from 'app/model/pipeline/power-query-sink';
import { irDefaultInstanceName } from 'app/integrationruntime/integrationruntime.constants';
import { DataFlowParser } from '../dataflow/dataflow.parser';
import { DataFlowMapColumn } from 'app/model/dataflow/dataflow-map.column';
import { ColumnReference } from 'app/model/dataset/columnReference';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecuteWranglingDataflowActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            dataflow: '*',
            compute: '*',
            sinks: '*',
            queries: '*',
            linkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            staging: {
                linkedService: '*',
                folderPath: '*'
            },
            integrationRuntime: '*'
        }
    }
})
export class ExecuteWranglingDataflowActivityAdapter extends ActivityAsyncAdapter<
    ExecuteWranglingDataflowActivity,
    Model.ExecuteWranglingDataflowActivity
> {
    public model(): IModel {
        return Metamodel.modelFor(ExecuteWranglingDataflowActivity);
    }

    protected async _toResource(
        activity: ExecuteWranglingDataflowActivity
    ): Promise<Model.ExecuteWranglingDataflowActivity> {
        const modelDataFlowActivity: Model.ExecuteWranglingDataflowActivity = super._getCommonActivityProperties(
            activity,
            ExecuteWranglingDataflowActivity.MODEL.name
        ) as Model.ExecuteWranglingDataflowActivity;

        modelDataFlowActivity.typeProperties = {
            dataflow: activity.wranglingDataflow ? this._toDataFlowReferenceModel(activity) : undefined,
            staging: {
                linkedService: activity.stagingLinkedService
                    ? linkedServiceReferenceToResource(
                          activity.stagingLinkedService,
                          this.context,
                          this.ensureLogger(),
                          activity.stagingLinkedServiceParameters
                      )
                    : undefined,
                folderPath: ValueAdapter.toResource(
                    activity,
                    ExecuteWranglingDataflowActivity.MODEL.attribute('stagingStoragePath')
                )
            }
        };
        if (activity.integrationRuntime && activity.integrationRuntime.name !== irDefaultInstanceName) {
            modelDataFlowActivity.typeProperties.integrationRuntime = ReferenceAdapter.toResource(
                activity.integrationRuntime,
                this.context
            ) as Model.IntegrationRuntimeReference;
            modelDataFlowActivity.typeProperties.compute = undefined;
        } else {
            modelDataFlowActivity.typeProperties.compute = {
                coreCount: ValueAdapter.toResource(activity, ExecuteWranglingDataflowActivity.CORE_COUNT),
                computeType: ValueAdapter.toResource(activity, ExecuteWranglingDataflowActivity.COMPUTE_TYPE)
            };
        }

        if (activity.queries) {
            const dataflowQueries: IDataFlowQuery[] = [];
            for (const powerQuerySink of activity.queries) {
                // TODO mablumen: call sink.toResource function instead
                const sinks: IDataFlowSink[] = [];
                for (const dataflowSink of powerQuerySink.sinks) {
                    const dataflowSinkDatasetReference = ReferenceAdapter.toResource(
                        dataflowSink.sinkDataset,
                        this.context
                    ) as Model.DatasetReference;
                    dataflowSinkDatasetReference.parameters = fillParameterValuesForResource(
                        activity.dataflowDatasetParameters.get(dataflowSink.name),
                        this.context,
                        this.ensureLogger()
                    );
                    const sinkResource: IDataFlowSink = {
                        name: dataflowSink.name,
                        dataset: dataflowSinkDatasetReference,
                        script: await this.getDslForSinkDataset(dataflowSink)
                    };
                    if (dataflowSink.faultToleranceLinkedService) {
                        sinkResource.rejectedDataLinkedService = ReferenceAdapter.toResource(
                            dataflowSink.faultToleranceLinkedService,
                            this.context
                        ) as Model.LinkedServiceReference;
                    }
                    sinks.push(sinkResource);
                }
                dataflowQueries.push({
                    queryName: powerQuerySink.queryName,
                    dataflowSinks: sinks
                });
            }
            modelDataFlowActivity.typeProperties.queries = dataflowQueries;
        }

        modelDataFlowActivity.typeProperties.dataFlowDebugSessionId = activity.dataFlowDebugSessionId;

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelDataFlowActivity, parsed);
        }
        return modelDataFlowActivity;
    }

    protected async _toModel(
        modelActivity: Model.ExecuteWranglingDataflowActivity
    ): Promise<ExecuteWranglingDataflowActivity> {
        const activity: ExecuteWranglingDataflowActivity =
            ExecuteWranglingDataflowActivity.MODEL.create<ExecuteWranglingDataflowActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);

        // Temporal workaround for SDK serialization issue (it serializes to dataFlow instead of dataflow). Bug #558995
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aModelActivity = modelActivity as any;
        if (
            aModelActivity.typeProperties &&
            aModelActivity.typeProperties.dataFlow &&
            !modelActivity.typeProperties.dataflow
        ) {
            modelActivity.typeProperties.dataflow = aModelActivity.typeProperties.dataFlow;
            delete aModelActivity.typeProperties.dataFlow;
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.dataflow) {
            activity.wranglingDataflow = ReferenceAdapter.toProxyModel<WranglingDataflow>(
                Metamodel.modelFor(WranglingDataflow),
                modelActivity.typeProperties.dataflow,
                this.context
            );
            if (modelActivity.typeProperties.dataflow.datasetParameters) {
                Object.keys(modelActivity.typeProperties.dataflow.datasetParameters).forEach((key) => {
                    if (modelActivity.typeProperties.dataflow.datasetParameters[key]) {
                        activity.dataflowDatasetParameters.set(key, []);
                        // parameters will be created with identity as if they belong to dataflow (instead of the dataset), because we don't have the reference
                        // to the dataset yet, but it will be replaced with the right object later on in parameters accordion component.
                        convertParametersToModel(
                            activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            activity.wranglingDataflow as any,
                            activity.dataflowDatasetParameters.get(key),
                            modelActivity.typeProperties.dataflow.datasetParameters[key]
                        );
                    }
                });
            }
        }

        if (modelActivity.typeProperties.staging && modelActivity.typeProperties.staging.linkedService) {
            activity.stagingLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                modelActivity.typeProperties.staging.linkedService,
                this.context
            );
            ValueAdapter.toModel(
                activity,
                ExecuteWranglingDataflowActivity.MODEL.attribute('stagingStoragePath'),
                modelActivity.typeProperties.staging.folderPath
            );
            if (modelActivity.typeProperties.staging.linkedService.parameters) {
                // Parameter value for linked service
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.stagingLinkedService,
                    activity.stagingLinkedServiceParameters,
                    modelActivity.typeProperties.staging.linkedService.parameters
                );
            }
        }

        if (modelActivity.typeProperties.sinks) {
            const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink));
            for (const queryName of Object.keys(modelActivity.typeProperties.sinks)) {
                const dataflowSink = modelActivity.typeProperties.sinks[queryName];
                const powerQuerySink: PowerQuerySink = Owned.create<PowerQuerySink>(PowerQuerySink.MODEL, activity);
                const proxy = Owned.create<DataFlowSink>(Metamodel.modelFor(DataFlowSink), powerQuerySink);

                await sinkAdapter.toModel(dataflowSink, proxy);
                proxy.isPowerQueryTransformation = true;
                powerQuerySink.queryName = queryName;
                powerQuerySink.sinks = [proxy];
                activity.queries.add(powerQuerySink);

                const dataflowParser = new DataFlowParser();
                await dataflowParser.parseSinkScriptAsync(this.context.contextData, dataflowSink.script, proxy);

                // distinctInputColumnRefs need to be defined manually for power query sink
                const inputColumns: ColumnReference[] = [];
                proxy.schema.forEach((mapColumn) => {
                    if (mapColumn instanceof DataFlowMapColumn) {
                        inputColumns.push(mapColumn.columnRef);
                    }
                });
                proxy.distinctInputColumnRefs = inputColumns;
            }
        }

        if (modelActivity.typeProperties.queries) {
            const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink));

            await Promise.all(
                modelActivity.typeProperties.queries.map(async (query) => {
                    const powerQuerySink: PowerQuerySink = Owned.create<PowerQuerySink>(PowerQuerySink.MODEL, activity);
                    powerQuerySink.queryName = query.queryName;

                    await Promise.all(
                        query.dataflowSinks.map(async (dataflowSink) => {
                            const proxy = Owned.create<DataFlowSink>(Metamodel.modelFor(DataFlowSink), powerQuerySink);
                            await sinkAdapter.toModel(dataflowSink, proxy);
                            proxy.isPowerQueryTransformation = true;

                            const dataflowParser = new DataFlowParser();
                            await dataflowParser.parseSinkScriptAsync(
                                this.context.contextData,
                                dataflowSink.script,
                                proxy
                            );

                            // distinctInputColumnRefs need to be defined manually for power query sink
                            const inputColumns: ColumnReference[] = [];
                            proxy.schema.forEach((mapColumn) => {
                                if (mapColumn instanceof DataFlowMapColumn) {
                                    inputColumns.push(mapColumn.columnRef);
                                }
                            });
                            proxy.distinctInputColumnRefs = inputColumns;
                            return proxy;
                        })
                    ).then((sinks: DataFlowSink[]) => {
                        powerQuerySink.sinks = sinks;
                        activity.queries.add(powerQuerySink);
                    });
                })
            );
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.integrationRuntime) {
            activity.integrationRuntime = ReferenceAdapter.toProxyModel<IntegrationRuntime>(
                Metamodel.modelFor(IntegrationRuntime),
                modelActivity.typeProperties.integrationRuntime,
                this.context
            );
        } else if (modelActivity.typeProperties.compute) {
            ValueAdapter.toModel(
                activity,
                ExecuteWranglingDataflowActivity.CORE_COUNT,
                modelActivity.typeProperties.compute.coreCount
            );
            ValueAdapter.toModel(
                activity,
                ExecuteWranglingDataflowActivity.COMPUTE_TYPE,
                modelActivity.typeProperties.compute.computeType
            );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelActivity, ExecuteWranglingDataflowActivity.MODEL)
        );

        return activity;
    }

    private _toDataFlowReferenceModel(dataflowActivity: ExecuteWranglingDataflowActivity): Model.DataFlowReference {
        const dataflowReference = ReferenceAdapter.toResource(
            dataflowActivity.wranglingDataflow,
            this.context
        ) as Model.DataFlowReference;
        // TODO - ans - remove this once backend correct the casing for reference type.
        dataflowReference.type = 'DataFlowReference';
        dataflowReference.datasetParameters = {};

        if (dataflowActivity && dataflowActivity.wranglingDataflow) {
            dataflowActivity.wranglingDataflow.sources.forEach((source) => {
                const datasetParameters = dataflowActivity.dataflowDatasetParameters.get(source.name);
                this.resetDatasetParameter(datasetParameters, source.sourceDataset);
                dataflowReference.datasetParameters[source.name] = fillParameterValuesForResource(
                    dataflowActivity.dataflowDatasetParameters.get(source.name),
                    this.context,
                    this.ensureLogger()
                );
            });
            dataflowActivity.queries.forEach((powerQuerySink) => {
                powerQuerySink.sinks.forEach((sink) => {
                    const datasetParameters = dataflowActivity.dataflowDatasetParameters.get(sink.name);
                    this.resetDatasetParameter(datasetParameters, sink.sinkDataset);
                    dataflowReference.datasetParameters[sink.name] = fillParameterValuesForResource(
                        dataflowActivity.dataflowDatasetParameters.get(sink.name),
                        this.context,
                        this.ensureLogger()
                    );
                });
            });
        }
        return dataflowReference;
    }

    // If parameter owner is dataflow, it's the placeholder we generated in toModel
    // Need to reset it to the real parameter of dataset
    private resetDatasetParameter(datasetParameters: ParameterValue[], dataset: Dataset): void {
        // The source or sink dataset may not have parameters.
        if (datasetParameters) {
            datasetParameters.forEach((parameterValue) => {
                if (parameterValue.parameter) {
                    parameterValue.parameter = dataset.parameters.find(
                        (parameter) => parameter.name === parameterValue.parameter.name
                    );
                }
            });
        }
    }

    // TODO mablumen: Verify partitionBy scenario in E2E
    private async getDslForSinkDataset(dataflowSink: DataFlowSink): Promise<string> {
        const adapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink));
        const sinkAdapter = adapter as SinkAdapter;
        let translatedDsl = await sinkAdapter.translateSinkPropertiesToDsl(dataflowSink);
        if (
            dataflowSink.fileNameOption === FileNameOption.SingleFile &&
            !translatedDsl.toLowerCase().includes('partitionby')
        ) {
            if (translatedDsl.toLowerCase().endsWith(')')) {
                translatedDsl = translatedDsl.slice(0, -1);
                translatedDsl = `${translatedDsl},\n\tpartitionBy('hash', 1))`;
            }
        }
        return `${translatedDsl} ${FLOW} ${dataflowSink.name}`;
    }
}
